
export interface DirectoryItem {
  id: string;
  name: string;
  subtitle?: string; // Email for users, description for others
  avatar?: string; // Initials or image url
}

export interface SearchParams {
  query: string;
  page: number;
  pageSize: number;
  sortBy?: 'name' | 'id';
  sortDir?: 'asc' | 'desc';
}

export interface SearchResult {
  items: DirectoryItem[];
  total: number;
  hasMore: boolean;
}

// Mock Data
const MOCK_PERSONS: DirectoryItem[] = Array.from({ length: 50 }).map((_, i) => ({
  id: `p${i + 1}`,
  name: `Person ${i + 1}`,
  subtitle: `person${i + 1}@company.com`,
  avatar: `P${i + 1}`
}));

const MOCK_GROUPS: DirectoryItem[] = [
  { id: 'grp_eng', name: 'Engineering', subtitle: 'Software Development', avatar: 'EN' },
  { id: 'grp_hr', name: 'Human Resources', subtitle: 'Personnel Management', avatar: 'HR' },
  { id: 'grp_sales', name: 'Sales', subtitle: 'Global Sales Team', avatar: 'SA' },
  { id: 'grp_marketing', name: 'Marketing', subtitle: 'Brand & Growth', avatar: 'MK' },
  { id: 'grp_it', name: 'IT Support', subtitle: 'Internal Infrastructure', avatar: 'IT' },
  { id: 'grp_finance', name: 'Finance', subtitle: 'Accounting & Payroll', avatar: 'FN' },
];

const MOCK_ROLES: DirectoryItem[] = [
  { id: 'role_admin', name: 'Administrator', subtitle: 'System Access', avatar: 'AD' },
  { id: 'role_manager', name: 'Manager', subtitle: 'Team Lead', avatar: 'MG' },
  { id: 'role_user', name: 'Standard User', subtitle: 'Basic Access', avatar: 'US' },
  { id: 'role_viewer', name: 'Viewer', subtitle: 'Read-only Access', avatar: 'VW' },
  { id: 'role_editor', name: 'Editor', subtitle: 'Content Creator', avatar: 'ED' },
];

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class MockDirectoryService {
  private async search(
    data: DirectoryItem[], 
    params: SearchParams
  ): Promise<SearchResult> {
    await delay(300 + Math.random() * 200); // Simulate network latency

    let filtered = data.filter(item => 
      item.name.toLowerCase().includes(params.query.toLowerCase()) || 
      item.id.toLowerCase().includes(params.query.toLowerCase())
    );

    if (params.sortBy) {
      filtered.sort((a, b) => {
        const valA = a[params.sortBy!] || '';
        const valB = b[params.sortBy!] || '';
        return params.sortDir === 'desc' 
          ? valB.localeCompare(valA) 
          : valA.localeCompare(valB);
      });
    }

    const start = (params.page - 1) * params.pageSize;
    const end = start + params.pageSize;
    const items = filtered.slice(start, end);

    return {
      items,
      total: filtered.length,
      hasMore: end < filtered.length
    };
  }

  async getPersons(params: SearchParams) { return this.search(MOCK_PERSONS, params); }
  async getGroups(params: SearchParams) { return this.search(MOCK_GROUPS, params); }
  async getRoles(params: SearchParams) { return this.search(MOCK_ROLES, params); }

  async getItemById(type: 'person' | 'group' | 'role', id: string): Promise<DirectoryItem | undefined> {
    await delay(100);
    const source = type === 'person' ? MOCK_PERSONS : type === 'group' ? MOCK_GROUPS : MOCK_ROLES;
    return source.find(i => i.id === id);
  }
}

export const directoryService = new MockDirectoryService();
